import { createLogger } from '@authe/fe-commons/logging';
import { asTrimmedString } from '@authe/fe-commons/display';
import * as React from 'react';
import { Badge, Button, ChevronRightIcon, cn, ExternalLinkIcon, Avatar, UserIcon, SparklesIcon, Textarea, StopIcon, SendIcon, Dialog, DialogContent, DialogHeader, DialogTitle, AlertIcon, ErrorState, Skeleton, EmptyState, RiskBadge, AuditTrail } from '@authe/ui-core';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { pendingReviewCount, predictionRiskLevel, isReviewActionable, topContributions, ML_REVIEW_STATUS_LABEL, MLReviewStatus, comparePredictions, disagreements, championOf } from '@authe/fe-commons/ml';
import { EventCriticality, EventResourceType } from '@authe/fe-commons/events';
import { RiskLevel } from '@authe/fe-commons/precedence';

// src/config.ts
var AI_ASSISTANT_PACKAGE = "@authe/ai-assistant";
var log = createLogger("ai-assistant");
var DEFAULT_CHAT_ENDPOINT = "/ai/chat";

// src/model/action.ts
function actionLabel(action) {
  if (typeof action?.title === "string" && action.title.trim()) return action.title;
  switch (action?.kind) {
    case "graph_overlay":
      return "Show on graph";
    case "table_filter":
      return "Filter table";
    case "navigate":
      return "Open";
    case "open_resource":
      return action.resource?.kind ? `Open ${action.resource.kind}` : "Open";
    case "run_query":
      return "Run query";
    case "suggest_catalog": {
      const s = action.suggestion;
      if (!s?.kind) return "Add to catalog";
      return s.name ? `Add ${s.kind} "${s.name}"` : `Add ${s.kind}`;
    }
    default:
      return "Action";
  }
}

// src/model/message.ts
var counter = 0;
function newId(prefix = "msg") {
  const uuid = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${(counter++).toString(36)}`;
  return `${prefix}_${uuid}`;
}
function messageText(message) {
  return message.parts.filter((p) => p.type === "text").map((p) => p.text).join("");
}
function messageActions(message) {
  return message.parts.filter((p) => p.type === "action").map((p) => p.action);
}
function messageCitations(message) {
  return message.parts.filter((p) => p.type === "citation").map((p) => p.citation);
}
function userMessage(text) {
  return { id: newId("u"), role: "user", parts: [{ type: "text", text }], createdAt: (/* @__PURE__ */ new Date()).toISOString() };
}
function assistantPlaceholder() {
  return { id: newId("a"), role: "assistant", parts: [], createdAt: (/* @__PURE__ */ new Date()).toISOString(), pending: true };
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
var RESOURCE_KINDS = /* @__PURE__ */ new Set([
  "transaction",
  "address",
  "entity",
  "label",
  "tag",
  "group"
]);
var CATALOG_KINDS = /* @__PURE__ */ new Set(["tag", "label", "entity", "group"]);
function normaliseResourceRef(raw) {
  if (!isRecord(raw)) return null;
  const kind = raw.kind;
  const id = asTrimmedString(raw.id);
  if (typeof kind !== "string" || !RESOURCE_KINDS.has(kind) || !id) {
    return null;
  }
  const chain = asTrimmedString(raw.chain);
  return { kind, id, ...chain ? { chain } : {} };
}
function safeAppPath(value) {
  const href = asTrimmedString(value);
  if (!href) return null;
  if (!href.startsWith("/") || href.startsWith("//") || href.startsWith("/\\")) return null;
  return href;
}
function safeExternalHref(value) {
  const href = asTrimmedString(value);
  if (!href) return null;
  try {
    const url = new URL(href);
    return url.protocol === "https:" || url.protocol === "http:" ? url.toString() : null;
  } catch {
    return null;
  }
}
function normaliseAction(raw) {
  if (!isRecord(raw)) return null;
  const title = asTrimmedString(raw.title);
  const withTitle = (action) => title ? { ...action, title } : action;
  switch (raw.kind) {
    case "graph_overlay":
      return isRecord(raw.overlay) ? withTitle({ kind: "graph_overlay", overlay: raw.overlay }) : null;
    case "table_filter":
      return isRecord(raw.filter) ? withTitle({ kind: "table_filter", filter: raw.filter }) : null;
    case "navigate": {
      const href = safeAppPath(raw.href);
      const target = normaliseResourceRef(raw.target);
      if (!href && !target) return null;
      return withTitle({
        kind: "navigate",
        ...href ? { href } : {},
        ...target ? { target } : {}
      });
    }
    case "open_resource": {
      const resource = normaliseResourceRef(raw.resource);
      return resource ? withTitle({ kind: "open_resource", resource }) : null;
    }
    case "run_query": {
      const query = asTrimmedString(raw.query);
      if (!query) return null;
      return withTitle({
        kind: "run_query",
        query,
        ...isRecord(raw.params) ? { params: raw.params } : {}
      });
    }
    case "suggest_catalog": {
      const s = raw.suggestion;
      if (!isRecord(s)) return null;
      const name = asTrimmedString(s.name);
      if (typeof s.kind !== "string" || !CATALOG_KINDS.has(s.kind) || !name) {
        return null;
      }
      const target = normaliseResourceRef(s.target);
      const suggestion = {
        kind: s.kind,
        name,
        ...asTrimmedString(s.color) ? { color: s.color } : {},
        ...target ? { target } : {},
        ...asTrimmedString(s.rationale) ? { rationale: s.rationale } : {}
      };
      return withTitle({ kind: "suggest_catalog", suggestion });
    }
    default:
      return null;
  }
}
function normaliseCitation(raw) {
  if (!isRecord(raw)) return null;
  const label = asTrimmedString(raw.label);
  if (!label) return null;
  const href = safeExternalHref(raw.href);
  const resource = normaliseResourceRef(raw.resource);
  const snippet = asTrimmedString(raw.snippet);
  return {
    // The chip list keys on this, so a missing id is minted rather than left
    // `undefined` for every citation — which reconciles them as one element.
    id: asTrimmedString(raw.id) ?? newId("cit"),
    label,
    ...href ? { href } : {},
    ...resource ? { resource } : {},
    ...snippet ? { snippet } : {}
  };
}
var UNSPECIFIED_ERROR = "The assistant reported an error without saying what went wrong.";
function normaliseStreamEvent(raw) {
  if (!isRecord(raw)) return null;
  switch (raw.type) {
    case "text-delta":
      return typeof raw.text === "string" && raw.text !== "" ? { type: "text-delta", text: raw.text } : null;
    case "action": {
      const action = normaliseAction(raw.action);
      return action ? { type: "action", action } : null;
    }
    case "citation": {
      const citation = normaliseCitation(raw.citation);
      return citation ? { type: "citation", citation } : null;
    }
    case "finish":
      return isRecord(raw.finish) ? { type: "finish", finish: raw.finish } : { type: "finish" };
    case "error": {
      const message = isRecord(raw.error) ? asTrimmedString(raw.error.message) : asTrimmedString(raw.error);
      return { type: "error", error: { message: message ?? UNSPECIFIED_ERROR } };
    }
    default:
      return null;
  }
}

// src/model/suggestion.ts
var SEVERITY_ORDER = { high: 0, medium: 1, info: 2 };
function rankSuggestions(suggestions) {
  return suggestions.map((s, index) => ({ s, index })).sort((a, b) => SEVERITY_ORDER[a.s.severity] - SEVERITY_ORDER[b.s.severity] || a.index - b.index).map(({ s }) => s);
}

// src/transport/failure.ts
function isAbort(err) {
  return typeof err === "object" && err !== null && err.name === "AbortError";
}
function networkFailureMessage(err) {
  if (err instanceof TypeError) {
    return "Couldn't reach the assistant. Check your connection and try again \u2014 if it keeps failing, the assistant may not be available in this environment.";
  }
  const message = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return message || "The assistant request failed.";
}
function errorEnvelopeMessage(raw) {
  if (typeof raw !== "object" || raw === null) return null;
  const body = raw;
  if (body.ok !== false) return null;
  const error = body.error;
  const fromError = typeof error === "string" ? error : typeof error === "object" && error !== null && typeof error.message === "string" ? error.message : void 0;
  const message = (fromError ?? (typeof body.message === "string" ? body.message : "")).trim();
  return message || "The assistant returned an error without a message.";
}

// src/transport/sse.ts
function createSseChatTransport(options = {}) {
  const {
    url = DEFAULT_CHAT_ENDPOINT,
    fetchImpl,
    headers,
    buildBody,
    credentials = "include"
  } = options;
  return {
    async *stream(request, opts) {
      const doFetch = fetchImpl ?? globalThis.fetch;
      if (!doFetch) {
        yield { type: "error", error: { message: "No fetch implementation available" } };
        return;
      }
      const resolvedHeaders = typeof headers === "function" ? await headers() : headers;
      let response;
      try {
        response = await doFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "text/event-stream", ...resolvedHeaders },
          body: buildBody ? buildBody(request) : JSON.stringify(request),
          signal: opts?.signal,
          credentials
        });
      } catch (err) {
        if (isAbort(err)) return;
        yield { type: "error", error: { message: networkFailureMessage(err) } };
        return;
      }
      if (response.headers.get("content-type")?.includes("application/json")) {
        let raw = null;
        try {
          raw = await response.json();
        } catch (err) {
          if (isAbort(err)) return;
        }
        yield {
          type: "error",
          error: {
            message: errorEnvelopeMessage(raw) ?? "The assistant answered with JSON where it should have streamed."
          }
        };
        return;
      }
      if (!response.ok || !response.body) {
        yield {
          type: "error",
          error: { message: `Chat request failed (${response.status} ${response.statusText})` }
        };
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      try {
        for (; ; ) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer = normaliseNewlines(buffer + decoder.decode(value, { stream: true }));
          let sep;
          while ((sep = buffer.indexOf("\n\n")) !== -1) {
            const frame = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const event = parseFrame(frame);
            if (event === "DONE") return;
            if (event) yield event;
          }
        }
        buffer = normaliseNewlines(buffer + decoder.decode(), true);
        const tail = parseFrame(buffer);
        if (tail && tail !== "DONE") yield tail;
      } catch (err) {
        if (isAbort(err)) return;
        yield { type: "error", error: { message: err?.message || "The stream was interrupted." } };
      } finally {
        void reader.cancel().catch(() => void 0);
      }
    }
  };
}
function parseFrame(frame) {
  const dataLines = frame.split("\n").filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim());
  if (dataLines.length === 0) return null;
  const data = dataLines.join("\n");
  if (data === "[DONE]") return "DONE";
  try {
    const event = normaliseStreamEvent(JSON.parse(data));
    if (event) return event;
    log.warn("Ignoring malformed SSE event", { data });
    return null;
  } catch {
    log.warn("Failed to parse SSE frame", { data });
    return null;
  }
}
function normaliseNewlines(text, final = false) {
  const held = !final && text.endsWith("\r");
  const body = (held ? text.slice(0, -1) : text).replace(/\r\n?/g, "\n");
  return held ? `${body}\r` : body;
}

// src/transport/text.ts
function createTextStreamChatTransport(options = {}) {
  const { url = DEFAULT_CHAT_ENDPOINT, fetchImpl, headers, buildBody, credentials = "include" } = options;
  return {
    async *stream(request, opts) {
      const doFetch = fetchImpl ?? globalThis.fetch;
      if (!doFetch) {
        yield { type: "error", error: { message: "No fetch implementation available" } };
        return;
      }
      const resolvedHeaders = typeof headers === "function" ? await headers() : headers;
      let response;
      try {
        response = await doFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json", ...resolvedHeaders },
          body: buildBody ? buildBody(request) : JSON.stringify(request),
          signal: opts?.signal,
          credentials
        });
      } catch (err) {
        if (isAbort(err)) return;
        yield { type: "error", error: { message: networkFailureMessage(err) } };
        return;
      }
      if (response.headers.get("content-type")?.includes("application/json")) {
        let raw = null;
        try {
          raw = await response.json();
        } catch (err) {
          if (isAbort(err)) return;
        }
        yield {
          type: "error",
          error: {
            message: errorEnvelopeMessage(raw) ?? "The assistant answered with JSON where it should have streamed text."
          }
        };
        return;
      }
      if (!response.ok || !response.body) {
        yield {
          type: "error",
          error: { message: `Chat request failed (${response.status} ${response.statusText})` }
        };
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      try {
        for (; ; ) {
          const { value, done } = await reader.read();
          if (done) break;
          const text = decoder.decode(value, { stream: true });
          if (text) yield { type: "text-delta", text };
        }
        const rest = decoder.decode();
        if (rest) yield { type: "text-delta", text: rest };
        yield { type: "finish" };
      } catch (err) {
        if (isAbort(err)) return;
        yield { type: "error", error: { message: err?.message || "The stream was interrupted." } };
      } finally {
        void reader.cancel().catch(() => void 0);
      }
    }
  };
}

// src/transport/json.ts
function defaultParseResponse(raw) {
  const root = raw && typeof raw === "object" ? raw : {};
  const body = root.data && typeof root.data === "object" ? root.data : root;
  const text = [body.text, body.content, body.answer, body.message].find(
    (v) => typeof v === "string" && v.trim() !== ""
  );
  return {
    text: text ?? "",
    // Through the gate, not a cast: see `model/normalise` for what a malformed
    // action used to do to the render.
    actions: Array.isArray(body.actions) ? body.actions.map(normaliseAction).filter((a) => a !== null) : void 0,
    citations: Array.isArray(body.citations) ? body.citations.map(normaliseCitation).filter((c) => c !== null) : void 0,
    finish: body.finish ?? (body.usage ? { usage: body.usage } : void 0)
  };
}
function createJsonChatTransport(options = {}) {
  const {
    url = DEFAULT_CHAT_ENDPOINT,
    fetchImpl,
    headers,
    buildBody,
    credentials = "include",
    parseResponse = defaultParseResponse
  } = options;
  return {
    async *stream(request, opts) {
      const doFetch = fetchImpl ?? globalThis.fetch;
      if (!doFetch) {
        yield { type: "error", error: { message: "No fetch implementation available" } };
        return;
      }
      const resolvedHeaders = typeof headers === "function" ? await headers() : headers;
      let response;
      try {
        response = await doFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json", accept: "application/json", ...resolvedHeaders },
          body: buildBody ? buildBody(request) : JSON.stringify(request),
          signal: opts?.signal,
          credentials
        });
      } catch (err) {
        if (isAbort(err)) return;
        yield { type: "error", error: { message: networkFailureMessage(err) } };
        return;
      }
      let raw;
      try {
        raw = await response.json();
      } catch (err) {
        if (isAbort(err)) return;
        yield {
          type: "error",
          error: {
            message: response.ok ? "The assistant replied with something that isn't JSON." : `The assistant request failed (${response.status}${response.statusText ? ` ${response.statusText}` : ""}).`
          }
        };
        return;
      }
      const failure = errorEnvelopeMessage(raw);
      if (failure !== null || !response.ok) {
        yield {
          type: "error",
          error: {
            message: failure ?? `The assistant request failed (${response.status}${response.statusText ? ` ${response.statusText}` : ""}).`
          }
        };
        return;
      }
      let parsed;
      try {
        parsed = parseResponse(raw);
      } catch (err) {
        yield { type: "error", error: { message: err?.message || "The assistant's reply could not be read." } };
        return;
      }
      if (opts?.signal?.aborted) return;
      if (parsed.text) yield { type: "text-delta", text: parsed.text };
      for (const action of parsed.actions ?? []) {
        const safe = normaliseAction(action);
        if (safe) yield { type: "action", action: safe };
      }
      for (const citation of parsed.citations ?? []) {
        const safe = normaliseCitation(citation);
        if (safe) yield { type: "citation", citation: safe };
      }
      yield { type: "finish", finish: parsed.finish };
    }
  };
}
var createRequestResponseChatTransport = createJsonChatTransport;

// src/transport/mock.ts
var delay = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
  const t = setTimeout(resolve, ms);
  signal?.addEventListener("abort", () => {
    clearTimeout(t);
    reject(new DOMException("Aborted", "AbortError"));
  });
});
function createMockChatTransport(responder) {
  return {
    async *stream(request, opts) {
      const resolved = typeof responder === "function" ? responder(request) : typeof responder === "string" ? { text: responder } : responder;
      try {
        if (Array.isArray(resolved)) {
          for (const event of resolved) {
            await delay(40, opts?.signal);
            yield event;
          }
          yield { type: "finish" };
          return;
        }
        const tokens = resolved.text.split(/(\s+)/).filter(Boolean);
        for (const token of tokens) {
          await delay(resolved.tokenDelayMs ?? 25, opts?.signal);
          yield { type: "text-delta", text: token };
        }
        for (const event of resolved.events ?? []) {
          await delay(40, opts?.signal);
          yield event;
        }
        yield { type: "finish" };
      } catch (err) {
        if (err?.name === "AbortError") return;
        yield { type: "error", error: { message: err?.message ?? "Mock error" } };
      }
    }
  };
}
function toRequestMessages(messages) {
  return messages.filter((m) => !m.pending && (m.role === "user" || m.role === "assistant")).map((m) => ({ role: m.role, content: messageText(m) })).filter((m) => m.content.length > 0);
}
function useAssistant(options) {
  const { transport, initialMessages, threadId, context, onAction, onCitation, onError } = options;
  const [messages, setMessages] = React.useState(initialMessages ?? []);
  const [status, setStatus] = React.useState("idle");
  const [error, setError] = React.useState(null);
  const [input, setInput] = React.useState("");
  const abortRef = React.useRef(null);
  const messagesRef = React.useRef(initialMessages ?? []);
  const commit = React.useCallback((update) => {
    const next = update(messagesRef.current);
    messagesRef.current = next;
    setMessages(next);
  }, []);
  const runRef = React.useRef(0);
  const cbRef = React.useRef({ onAction, onCitation, onError });
  cbRef.current = { onAction, onCitation, onError };
  const appendPart = React.useCallback(
    (messageId, part) => {
      commit(
        (prev) => prev.map((m) => {
          if (m.id !== messageId) return m;
          if (part.type === "text") {
            const last = m.parts[m.parts.length - 1];
            if (last && last.type === "text") {
              const parts = m.parts.slice(0, -1).concat({ type: "text", text: last.text + part.text });
              return { ...m, parts };
            }
          }
          return { ...m, parts: [...m.parts, part] };
        })
      );
    },
    [commit]
  );
  const run = React.useCallback(
    async (history) => {
      const runId = ++runRef.current;
      const isCurrent = () => runRef.current === runId;
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("streaming");
      setError(null);
      const placeholder = assistantPlaceholder();
      commit(() => [...history, placeholder]);
      const settle = (patch) => commit((prev) => prev.map((m) => m.id === placeholder.id ? { ...m, pending: false, ...patch } : m));
      const fail = (err, logMessage) => {
        settle({ error: err.message });
        if (!isCurrent()) return;
        setError(err);
        setStatus("error");
        cbRef.current.onError?.(err);
        log.error(logMessage, { message: err.message });
      };
      try {
        const stream = transport.stream(
          { messages: toRequestMessages(history), threadId, context },
          { signal: controller.signal }
        );
        for await (const raw of stream) {
          if (!isCurrent()) return;
          const event = normaliseStreamEvent(raw);
          if (!event) continue;
          switch (event.type) {
            case "text-delta":
              appendPart(placeholder.id, { type: "text", text: event.text });
              break;
            case "action":
              appendPart(placeholder.id, { type: "action", action: event.action });
              cbRef.current.onAction?.(event.action);
              break;
            case "citation":
              appendPart(placeholder.id, { type: "citation", citation: event.citation });
              cbRef.current.onCitation?.(event.citation);
              break;
            case "error":
              fail(new Error(event.error.message), "Assistant stream error");
              return;
            case "finish":
              break;
          }
        }
        settle({});
        if (isCurrent()) setStatus("idle");
      } catch (err) {
        if (err?.name === "AbortError") {
          settle({});
          if (isCurrent()) setStatus("idle");
          return;
        }
        const e = err instanceof Error ? err : new Error(String(err));
        fail(e, "Assistant transport threw");
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [transport, threadId, context, appendPart, commit]
  );
  const busy = () => abortRef.current !== null;
  const send = React.useCallback(
    async (text) => {
      const content = (text ?? input).trim();
      if (!content || busy()) return;
      setInput("");
      const history = [...messagesRef.current, userMessage(content)];
      commit(() => history);
      await run(history);
    },
    [input, run, commit]
  );
  const stop = React.useCallback(() => {
    abortRef.current?.abort();
  }, []);
  const regenerate = React.useCallback(async () => {
    if (busy()) return;
    const current = messagesRef.current;
    let lastUser = -1;
    for (let i = current.length - 1; i >= 0; i--) {
      if (current[i].role === "user") {
        lastUser = i;
        break;
      }
    }
    if (lastUser === -1) return;
    const history = current.slice(0, lastUser + 1);
    commit(() => history);
    await run(history);
  }, [run, commit]);
  const clear = React.useCallback(() => {
    runRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    commit(() => initialMessages ?? []);
    setStatus("idle");
    setError(null);
  }, [initialMessages, commit]);
  return { messages, status, error, input, setInput, send, stop, regenerate, clear };
}
var useAiAssistant = useAssistant;

// src/tools/registry.ts
var DEFAULT_AUTHE_TOOLS = [
  {
    name: "query_transactions",
    description: "Search transactions by address, entity, chain, value, or time range.",
    category: "query"
  },
  {
    name: "summarize_entity",
    description: "Summarize what an entity is, its risk, and notable counterparties.",
    category: "query"
  },
  {
    name: "build_graph",
    description: "Build a fund-flow graph around an address/entity for the canvas.",
    category: "graph"
  },
  {
    name: "suggest_tags",
    description: "Propose tags/labels for an address or entity with rationale.",
    category: "catalog"
  },
  {
    name: "explain_risk",
    description: "Explain a risk score via its top weighted feature contributions.",
    category: "risk"
  },
  {
    name: "find_related",
    description: "Find related transactions, addresses, or entities (clusters, bridges).",
    category: "query"
  }
];
function registerAutheTools(extra = [], opts = {}) {
  const { includeDefaults = true } = opts;
  const specs = /* @__PURE__ */ new Map();
  for (const spec of includeDefaults ? DEFAULT_AUTHE_TOOLS : []) specs.set(spec.name, spec);
  for (const spec of extra) specs.set(spec.name, spec);
  const handlers = /* @__PURE__ */ new Map();
  return {
    list: () => [...specs.values()],
    get: (name) => specs.get(name),
    has: (name) => specs.has(name),
    setHandler: (name, handler) => {
      handlers.set(name, handler);
    },
    dispatch: async (name, args) => {
      const handler = handlers.get(name);
      if (!handler) throw new Error(`No client handler registered for tool "${name}"`);
      return handler(args);
    }
  };
}

// src/graph/overlay.ts
function overlayToTxGraphInput(overlay) {
  const nodes = (overlay.nodes ?? []).map((node) => ({
    id: node.id,
    data: {
      label: node.label,
      subtitle: node.subtitle,
      chain: node.chain,
      badge: node.badge,
      variant: node.variant
    }
  }));
  const edges = (overlay.edges ?? []).map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    data: {
      label: edge.label,
      variant: edge.variant,
      timestamp: edge.timestamp,
      dashed: false
    }
  }));
  return {
    nodes,
    edges,
    focusNodeId: overlay.focusNodeId,
    highlightNodeIds: (overlay.nodes ?? []).filter((n) => n.highlight).map((n) => n.id),
    highlightEdgeIds: (overlay.edges ?? []).filter((e) => e.highlight).map((e) => e.id)
  };
}
function mergeById(existing, incoming) {
  const map = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) map.set(item.id, item);
  return [...map.values()];
}
var KIND_BADGE = {
  graph_overlay: "Graph",
  table_filter: "Filter",
  navigate: "Navigate",
  open_resource: "Open",
  run_query: "Query",
  suggest_catalog: "Catalog"
};
function ActionChip({
  action,
  onAction,
  canApply,
  disabled,
  className
}) {
  const applicable = Boolean(onAction) && (canApply ? canApply(action) : true);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2.5 py-1.5 text-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "shrink-0 text-[10px] uppercase tracking-wide", children: KIND_BADGE[action.kind] ?? "Action" }),
          /* @__PURE__ */ jsx("span", { className: "truncate", children: actionLabel(action) })
        ] }),
        applicable ? /* @__PURE__ */ jsxs(
          Button,
          {
            type: "button",
            size: "sm",
            variant: "ghost",
            disabled,
            onClick: () => onAction?.(action),
            className: "shrink-0 gap-1",
            children: [
              "Apply",
              /* @__PURE__ */ jsx(ChevronRightIcon, { width: 14, height: 14 })
            ]
          }
        ) : (
          // Said, not hidden: the suggestion is still information worth reading,
          // it just cannot be carried out from here.
          /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xs text-muted-foreground", title: "This surface can't apply this kind of suggestion.", children: "Not available here" })
        )
      ]
    }
  );
}
function Citations({ citations, onOpen, className }) {
  if (citations.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-wrap items-center gap-1.5 pt-1", className), children: [
    /* @__PURE__ */ jsx("span", { className: "text-[10px] uppercase tracking-wide text-muted-foreground", children: "Sources" }),
    citations.map((citation, index) => {
      const href = safeExternalHref(citation.href);
      const key = citation.id || `${citation.label}-${index}`;
      return href ? /* @__PURE__ */ jsx("a", { href, target: "_blank", rel: "noreferrer noopener", title: citation.snippet, children: /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "cursor-pointer gap-1 hover:bg-accent", children: [
        citation.label,
        /* @__PURE__ */ jsx(ExternalLinkIcon, { width: 11, height: 11 })
      ] }) }, key) : /* @__PURE__ */ jsx("button", { type: "button", onClick: () => onOpen?.(citation), title: citation.snippet, children: /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "cursor-pointer hover:bg-accent", children: citation.label }) }, key);
    })
  ] });
}
function SuggestionChips({
  suggestions,
  onPick,
  disabled,
  className
}) {
  if (suggestions.length === 0) return null;
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap gap-2", className), children: suggestions.map((prompt) => /* @__PURE__ */ jsx("button", { type: "button", disabled, onClick: () => onPick(prompt), children: /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "cursor-pointer px-2.5 py-1 hover:bg-secondary/70", children: prompt }) }, prompt)) });
}
function TypingDots() {
  return /* @__PURE__ */ jsx("span", { className: "inline-flex gap-1 py-1", "aria-label": "Assistant is typing", children: [0, 1, 2].map((i) => /* @__PURE__ */ jsx(
    "span",
    {
      className: "h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/60",
      style: { animationDelay: `${i * 150}ms` }
    },
    i
  )) });
}
function AssistantMessage({
  message,
  onAction,
  canApplyAction,
  onOpenCitation,
  actionsDisabled,
  userAvatar,
  className
}) {
  const isUser = message.role === "user";
  const citations = messageCitations(message);
  const hasText = message.parts.some((p) => p.type === "text" && typeof p.text === "string" && p.text.length > 0);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex gap-3", isUser ? "flex-row-reverse" : "flex-row", className), children: [
    /* @__PURE__ */ jsx(
      Avatar,
      {
        size: "sm",
        className: isUser ? "bg-primary/10 text-primary" : "bg-brand/10 text-brand",
        src: isUser ? userAvatar?.src : void 0,
        alt: isUser ? userAvatar?.alt : void 0,
        fallback: isUser ? userAvatar?.fallback ?? /* @__PURE__ */ jsx(UserIcon, { width: 14, height: 14 }) : /* @__PURE__ */ jsx(SparklesIcon, { width: 14, height: 14 })
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: cn("flex min-w-0 max-w-[85%] flex-col gap-2", isUser ? "items-end" : "items-start"), children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "rounded-lg px-3 py-2 text-sm",
            isUser ? "bg-primary text-primary-foreground" : "border bg-card"
          ),
          children: [
            message.parts.map((part, index) => {
              if (part.type === "text") {
                return /* @__PURE__ */ jsx("p", { className: "whitespace-pre-wrap break-words", children: part.text }, index);
              }
              if (part.type === "action") {
                return /* @__PURE__ */ jsx("div", { className: "mt-2 first:mt-0", children: /* @__PURE__ */ jsx(
                  ActionChip,
                  {
                    action: part.action,
                    onAction,
                    ...canApplyAction ? { canApply: canApplyAction } : {},
                    disabled: actionsDisabled
                  }
                ) }, index);
              }
              return null;
            }),
            message.pending && !hasText ? /* @__PURE__ */ jsx(TypingDots, {}) : null,
            message.error ? /* @__PURE__ */ jsx("p", { className: "mt-1 text-xs text-destructive", children: message.error }) : null
          ]
        }
      ),
      !isUser && /* @__PURE__ */ jsx(Citations, { citations, onOpen: onOpenCitation })
    ] })
  ] });
}
function AssistantComposer({
  value,
  onChange,
  onSend,
  onStop,
  streaming,
  disabled,
  placeholder = "Ask about a transaction, address, or entity\u2026",
  className
}) {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!streaming && value.trim()) onSend();
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-end gap-2 border-t bg-background p-2", className), children: [
    /* @__PURE__ */ jsx(
      Textarea,
      {
        rows: 1,
        value,
        disabled,
        placeholder,
        onChange: (event) => onChange(event.target.value),
        onKeyDown: handleKeyDown,
        className: "max-h-40 min-h-9 flex-1 resize-none"
      }
    ),
    streaming ? /* @__PURE__ */ jsx(Button, { type: "button", size: "icon", variant: "outline", onClick: onStop, "aria-label": "Stop", children: /* @__PURE__ */ jsx(StopIcon, {}) }) : /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        size: "icon",
        variant: "brand",
        disabled: disabled || !value.trim(),
        onClick: onSend,
        "aria-label": "Send",
        children: /* @__PURE__ */ jsx(SendIcon, {})
      }
    )
  ] });
}
function AssistantPanel({
  transport,
  title = "Assistant",
  subtitle,
  suggestions = [],
  initialMessages,
  threadId,
  context,
  onAction,
  onOpenCitation,
  placeholder,
  emptyTitle = "Ask anything",
  emptyDescription = "Query transactions, summarize an entity, or build a graph \u2014 answers come back with sources and one-click actions.",
  header,
  className
}) {
  const assistant = useAssistant({ transport, initialMessages, threadId, context, onAction });
  const { messages, status, error, input, setInput, send, stop } = assistant;
  const scrollRef = React.useRef(null);
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);
  const isEmpty = messages.length === 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-background", className), children: [
    header ?? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 border-b px-3 py-2", children: [
      /* @__PURE__ */ jsx("span", { className: "inline-flex h-6 w-6 items-center justify-center rounded-md bg-brand/10 text-brand", children: /* @__PURE__ */ jsx(SparklesIcon, { width: 14, height: 14 }) }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
        /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold leading-none", children: title }),
        subtitle ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: subtitle }) : null
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { ref: scrollRef, className: "flex-1 space-y-4 overflow-y-auto p-3", children: [
      isEmpty ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center gap-4 py-8 text-center", children: [
        /* @__PURE__ */ jsx("span", { className: "inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand/10 text-brand", children: /* @__PURE__ */ jsx(SparklesIcon, { width: 20, height: 20 }) }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx("p", { className: "text-sm font-medium", children: emptyTitle }),
          /* @__PURE__ */ jsx("p", { className: "mx-auto max-w-sm text-xs text-muted-foreground", children: emptyDescription })
        ] }),
        /* @__PURE__ */ jsx(SuggestionChips, { suggestions, onPick: (prompt) => void send(prompt), className: "justify-center" })
      ] }) : messages.map((message) => /* @__PURE__ */ jsx(
        AssistantMessage,
        {
          message,
          onAction,
          onOpenCitation,
          actionsDisabled: status === "streaming"
        },
        message.id
      )),
      error && !messages.some((m) => m.error) ? /* @__PURE__ */ jsx("p", { className: "text-center text-xs text-destructive", children: error.message }) : null
    ] }),
    /* @__PURE__ */ jsx(
      AssistantComposer,
      {
        value: input,
        onChange: setInput,
        onSend: () => void send(),
        onStop: stop,
        streaming: status === "streaming",
        placeholder
      }
    )
  ] });
}
var AiChatPanel = AssistantPanel;
function AssistantLauncher({
  buttonLabel,
  open,
  onOpenChange,
  hideTrigger,
  triggerClassName,
  title = "Assistant",
  ...panelProps
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = (next) => {
    setInternalOpen(next);
    onOpenChange?.(next);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    !hideTrigger && /* @__PURE__ */ jsxs(
      Button,
      {
        type: "button",
        variant: "brand",
        onClick: () => setOpen(true),
        className: cn(
          "fixed bottom-5 right-5 z-40 gap-2 rounded-full shadow-lg",
          buttonLabel ? "px-4" : "h-12 w-12 p-0",
          triggerClassName
        ),
        "aria-label": buttonLabel ?? "Open assistant",
        children: [
          /* @__PURE__ */ jsx(SparklesIcon, { width: 18, height: 18 }),
          buttonLabel
        ]
      }
    ),
    /* @__PURE__ */ jsx(Dialog, { open: isOpen, onOpenChange: setOpen, children: /* @__PURE__ */ jsxs(DialogContent, { className: "flex h-[70vh] max-h-[680px] w-full max-w-lg flex-col gap-0 overflow-hidden p-0", children: [
      /* @__PURE__ */ jsx(DialogHeader, { className: "border-b px-3 py-2", children: /* @__PURE__ */ jsxs(DialogTitle, { className: "flex items-center gap-2 text-sm", children: [
        /* @__PURE__ */ jsx("span", { className: "inline-flex h-6 w-6 items-center justify-center rounded-md bg-brand/10 text-brand", children: /* @__PURE__ */ jsx(SparklesIcon, { width: 14, height: 14 }) }),
        title
      ] }) }),
      /* @__PURE__ */ jsx("div", { className: "min-h-0 flex-1", children: /* @__PURE__ */ jsx(AssistantPanel, { ...panelProps, title, header: /* @__PURE__ */ jsx(Fragment, {}), className: "h-full rounded-none border-0" }) })
    ] }) })
  ] });
}
var SEVERITY = {
  // The risk ladder's own tokens, so a "high" here is the colour of a high
  // everywhere else in the product.
  high: { stripe: "bg-risk-high", word: "Needs attention" },
  medium: { stripe: "bg-risk-medium", word: "Worth a look" },
  info: { stripe: "bg-muted-foreground/40", word: "For context" }
};
function SuggestionFeed({
  suggestions,
  onAccept,
  acceptLabel = "Accept",
  onDismiss,
  showLabel,
  onShow,
  onOpenCitation,
  pendingId,
  empty,
  className
}) {
  const ranked = React.useMemo(() => rankSuggestions(suggestions), [suggestions]);
  if (ranked.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: cn("text-sm text-muted-foreground", className), children: empty ?? "Nothing to suggest." });
  }
  return /* @__PURE__ */ jsx("ul", { className: cn("flex flex-col gap-3", className), "aria-label": "Suggestions", children: ranked.map((suggestion) => {
    const tone = SEVERITY[suggestion.severity];
    const show = onShow ? showLabel?.(suggestion) ?? null : null;
    const busy = pendingId === suggestion.id;
    return /* @__PURE__ */ jsxs("li", { className: "relative overflow-hidden rounded-md border bg-card pl-4 pr-3 py-3", children: [
      /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: cn("absolute inset-y-0 left-0 w-1", tone.stripe) }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
        suggestion.severity === "high" ? /* @__PURE__ */ jsx(AlertIcon, { width: 14, height: 14, className: "mt-0.5 shrink-0 text-risk-high", "aria-hidden": true }) : /* @__PURE__ */ jsx(SparklesIcon, { width: 14, height: 14, className: "mt-0.5 shrink-0 text-muted-foreground", "aria-hidden": true }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1 space-y-1", children: [
          /* @__PURE__ */ jsxs("p", { className: "text-sm font-medium leading-snug", children: [
            suggestion.title,
            /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
              " \u2014 ",
              tone.word
            ] })
          ] }),
          suggestion.kind === "hypothesis" ? /* @__PURE__ */ jsx("p", { className: "text-[11px] uppercase tracking-wide text-muted-foreground", children: "Hypothesis" }) : null,
          suggestion.detail ? /* @__PURE__ */ jsx("p", { className: "whitespace-pre-line text-sm text-muted-foreground", children: suggestion.detail }) : null,
          suggestion.citations && suggestion.citations.length > 0 ? /* @__PURE__ */ jsx(Citations, { citations: suggestion.citations, onOpen: onOpenCitation, className: "pt-0.5" }) : null
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "mt-3 flex flex-wrap items-center gap-2 pl-6", children: [
        onAccept ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "brand", disabled: busy, onClick: () => onAccept(suggestion), children: busy ? "Adding\u2026" : acceptLabel }) : null,
        show ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", disabled: busy, onClick: () => onShow?.(suggestion), children: show }) : null,
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            size: "sm",
            variant: "ghost",
            disabled: busy,
            onClick: () => onDismiss(suggestion),
            className: "ml-auto text-muted-foreground",
            children: "Dismiss"
          }
        )
      ] })
    ] }, suggestion.id);
  }) });
}
var REASON_LABEL = {
  low_confidence: "Low confidence",
  high_risk: "High risk",
  shadow_disagreement: "Shadow disagreement",
  sampled: "Sampled",
  manual: "Manual"
};
var STATUS_TONE = {
  pending: "border-amber-500/30 bg-amber-500/10 text-amber-600",
  approved: "border-risk-none/30 bg-risk-none/10 text-risk-none",
  rejected: "border-risk-high/30 bg-risk-high/10 text-risk-high",
  escalated: "border-risk-critical/30 bg-risk-critical/10 text-risk-critical"
};
function ReviewQueuePanel({
  items,
  onDecide,
  decidedBy,
  bands,
  readOnly,
  isLoading,
  error,
  onRetry,
  className
}) {
  if (error) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry });
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-2", className), "aria-busy": true, children: Array.from({ length: 4 }).map((_, index) => /* @__PURE__ */ jsx(Skeleton, { className: "h-24 w-full" }, index)) });
  }
  if (items.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Review queue empty",
        description: "Predictions that need a human decision will appear here."
      }
    );
  }
  const decide = (item, status) => {
    void onDecide?.(item.id, { status, decidedBy });
  };
  const pending = pendingReviewCount(items);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-sm font-semibold", children: "Review queue" }),
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
        pending,
        " pending"
      ] })
    ] }),
    items.map((item) => {
      const level = predictionRiskLevel(item.prediction, bands);
      const actionable = isReviewActionable(item) && !readOnly;
      const contributions = topContributions(item.prediction, 3);
      return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2 rounded-md border bg-card p-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium capitalize", children: item.prediction.target.kind }),
              /* @__PURE__ */ jsx("code", { className: "truncate rounded bg-muted px-1.5 py-0.5 text-xs", children: item.prediction.target.id }),
              level ? /* @__PURE__ */ jsx(RiskBadge, { level, score: item.prediction.score ?? void 0 }) : null
            ] }),
            /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
              item.prediction.modelId,
              "@",
              item.prediction.modelVersion,
              item.reason ? /* @__PURE__ */ jsxs(Fragment, { children: [
                " \xB7 ",
                REASON_LABEL[item.reason]
              ] }) : null
            ] })
          ] }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: cn("shrink-0 border", STATUS_TONE[item.status]), children: ML_REVIEW_STATUS_LABEL[item.status] })
        ] }),
        contributions.length > 0 && /* @__PURE__ */ jsx("ul", { className: "flex flex-wrap gap-1.5", children: contributions.map((c) => /* @__PURE__ */ jsxs(
          "li",
          {
            className: cn(
              "rounded border px-1.5 py-0.5 text-[11px]",
              c.weight >= 0 ? "text-risk-high" : "text-risk-none"
            ),
            title: c.value != null ? String(c.value) : void 0,
            children: [
              c.weight >= 0 ? "+" : "\u2212",
              " ",
              c.feature
            ]
          },
          c.feature
        )) }),
        actionable && /* @__PURE__ */ jsxs("div", { className: "flex gap-2 pt-1", children: [
          /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: () => decide(item, MLReviewStatus.APPROVED), children: "Approve" }),
          /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: () => decide(item, MLReviewStatus.REJECTED), children: "Reject" }),
          /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => decide(item, MLReviewStatus.ESCALATED), children: "Escalate" })
        ] }),
        item.rationale ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          "\u201C",
          item.rationale,
          "\u201D"
        ] }) : null
      ] }, item.id);
    })
  ] });
}
var RISK_TO_CRITICALITY = {
  [RiskLevel.SEVERE]: EventCriticality.CRITICAL,
  [RiskLevel.HIGH]: EventCriticality.HIGH,
  [RiskLevel.MEDIUM]: EventCriticality.MEDIUM,
  [RiskLevel.LOW]: EventCriticality.LOW,
  [RiskLevel.SAFE]: EventCriticality.SUCCESS
};
function predictionsToActivityLog(predictions, bands) {
  return predictions.map((prediction) => {
    const level = predictionRiskLevel(prediction, bands);
    const top = topContributions(prediction, 3).map((c) => `${c.weight >= 0 ? "+" : "\u2212"}${c.feature}`).join(", ");
    return {
      id: prediction.id,
      created_at: prediction.createdAt,
      resource: EventResourceType.ACTIVITY_LOG,
      criticality_level: level ? RISK_TO_CRITICALITY[level] : EventCriticality.LOW,
      message_summary: `Scored ${prediction.target.kind} ${prediction.target.id}${prediction.score != null ? ` \u2192 ${prediction.score}` : ""}${level ? ` (${level})` : ""}`,
      function_name: `${prediction.modelId}@${prediction.modelVersion}`,
      payload_summary: top || void 0,
      created_by: prediction.modelId,
      metadata: { latencyMs: prediction.latencyMs, confidence: prediction.confidence }
    };
  });
}
function PredictionAuditTrail({
  predictions,
  bands,
  className
}) {
  if (predictions.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No predictions yet", description: "Model predictions will appear here as they run." });
  }
  return /* @__PURE__ */ jsx(AuditTrail, { events: predictionsToActivityLog(predictions, bands), className: cn(className) });
}
function Stat({ label, value }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col rounded-md border bg-card px-3 py-2", children: [
    /* @__PURE__ */ jsx("span", { className: "text-lg font-semibold tabular-nums", children: value }),
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: label })
  ] });
}
function ModelComparison({
  champion,
  shadow,
  models,
  bands,
  className
}) {
  const comparison = React.useMemo(
    () => comparePredictions(champion, shadow, bands),
    [champion, shadow, bands]
  );
  const diffs = React.useMemo(() => disagreements(comparison), [comparison]);
  if (comparison.pairs.length === 0 && champion.length === 0 && shadow.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No predictions to compare", description: "Run the champion and shadow models to compare." });
  }
  const championModel = models ? championOf(models) : void 0;
  const shadowModel = models?.find((m) => m.role !== "champion");
  const agreementPct = Math.round(comparison.agreementRate * 1e3) / 10;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-sm", children: [
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
        "Champion \xB7 ",
        championModel?.name ?? comparison.championKey
      ] }),
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "vs" }),
      /* @__PURE__ */ jsxs(Badge, { variant: "secondary", children: [
        "Shadow \xB7 ",
        shadowModel?.name ?? comparison.shadowKey
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-2 sm:grid-cols-4", children: [
      /* @__PURE__ */ jsx(Stat, { label: "Agreement", value: `${agreementPct}%` }),
      /* @__PURE__ */ jsx(Stat, { label: "Compared", value: comparison.pairs.length }),
      /* @__PURE__ */ jsx(Stat, { label: "Champion only", value: comparison.championOnly }),
      /* @__PURE__ */ jsx(Stat, { label: "Shadow only", value: comparison.shadowOnly })
    ] }),
    /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("h4", { className: "mb-2 text-sm font-semibold", children: [
        "Disagreements ",
        /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
          "(",
          diffs.length,
          ")"
        ] })
      ] }),
      diffs.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "The two models agreed on every shared target." }) : /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1.5", children: diffs.map((pair) => /* @__PURE__ */ jsxs(
        "li",
        {
          className: "flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-sm",
          children: [
            /* @__PURE__ */ jsx("code", { className: "truncate text-xs", children: pair.targetKey }),
            /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [
              pair.championLevel ? /* @__PURE__ */ jsx(RiskBadge, { level: pair.championLevel }) : /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "\u2014" }),
              /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2192" }),
              pair.shadowLevel ? /* @__PURE__ */ jsx(RiskBadge, { level: pair.shadowLevel }) : /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "\u2014" }),
              pair.scoreDelta != null ? /* @__PURE__ */ jsxs(
                "span",
                {
                  className: cn(
                    "tabular-nums text-xs",
                    pair.scoreDelta > 0 ? "text-risk-high" : "text-risk-none"
                  ),
                  children: [
                    pair.scoreDelta > 0 ? "+" : "",
                    pair.scoreDelta
                  ]
                }
              ) : null
            ] })
          ]
        },
        pair.targetKey
      )) })
    ] })
  ] });
}

export { AI_ASSISTANT_PACKAGE, ActionChip, AiChatPanel, AssistantComposer, AssistantLauncher, AssistantMessage, AssistantPanel, Citations, DEFAULT_AUTHE_TOOLS, DEFAULT_CHAT_ENDPOINT, ModelComparison, PredictionAuditTrail, ReviewQueuePanel, SuggestionChips, SuggestionFeed, actionLabel, assistantPlaceholder, createJsonChatTransport, createMockChatTransport, createRequestResponseChatTransport, createSseChatTransport, createTextStreamChatTransport, errorEnvelopeMessage, isAbort, log, mergeById, messageActions, messageCitations, messageText, networkFailureMessage, newId, normaliseAction, normaliseCitation, normaliseNewlines, normaliseResourceRef, normaliseStreamEvent, overlayToTxGraphInput, predictionsToActivityLog, rankSuggestions, registerAutheTools, safeAppPath, safeExternalHref, useAiAssistant, useAssistant, userMessage };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map